import api from "./services/backendService";
import { store as reduxStore } from "./index";
import i18next, { t } from "i18next";
import keys from "./configs/constants";
import { POINT_VALUE } from "./configs/pointValues";
import { cloneElement } from "react";
import moment from "moment";

export { POINT_VALUE };

const MSG_TYPE_LOG = 0;
const MSG_TYPE_WARN = 1;
const MSG_TYPE_ERR = 2;

const isChrome = () => {
  return !!(
    globalThis.chrome &&
    globalThis.navigator.vendor === "Google Inc." &&
    globalThis.opr === undefined &&
    globalThis.navigator.userAgent.indexOf("Edge") === -1
  );
};

const debug = (...args) => {
  if (import.meta.env.DEV) {
    let level = "info";

    // Detect and extract log level from the first argument if it's a known level string
    const VALID_LEVELS = ["debug", "info", "warn", "error"];

    const levelKey = typeof args[0] === "string" ? args[0].toLowerCase() : null;
    if (levelKey && VALID_LEVELS.includes(levelKey)) {
      level = levelKey;
      args.shift(); // Remove the level label from args
    }

    const stack = new Error().stack;
    const stackLines = stack.split("\n");
    const callerInfo = stackLines[3] || stackLines[2] || "";
    const match =
      /\s+at\s+(.*?)\s+\((.*):(\d+):(\d+)\)/.exec(callerInfo) || //NOSONAR
      /\s+at\s+(.*):(\d+):(\d+)/.exec(callerInfo); //NOSONAR

    let functionName = "[anonymous]";
    let location = "";

    if (match) {
      const hasFunction = match.length === 5;
      const fullPath = match[hasFunction ? 2 : 1];
      const lineNumber = match[hasFunction ? 3 : 2];
      const fileName = fullPath.includes("src/")
        ? fullPath.split("src/").pop()
        : fullPath.split("/").pop();

      location = `${fileName}:${lineNumber}`;
      functionName = hasFunction
        ? match[1].split(".").pop() || "[anonymous]"
        : "[anonymous]";
    }

    const prefix = `🔧 %c[${functionName} @ ${location}]`;
    let style = "color: dodgerblue";

    if (level === "warn") {
      style = "color: orange";
    } else if (level === "error") {
      style = "color: crimson";
    }

    const logArgs = [prefix, style, ...args];

    if (level === "warn") {
      console.warn(...logArgs);
    } else if (level === "error") {
      console.error(...logArgs);
    } else {
      console.log(...logArgs);
    }
  }
};

// Check if the current path is one of the unauthenticated paths
const isUnauthPath = () => {
  return globalThis.location.pathname.includes("/docs");
};

const isDevEnvironment = () => {
  return import.meta.env.DEV;
};

const isUserAdmin = () => {
  return reduxStore.getState().user.role === "Admin";
};

const int2hex = (number, padding = 4) => {
  let num = number;
  let string;

  if (num < 0) {
    num = 0xffffffff + num + 1;
  }

  string = num.toString(16).toUpperCase();

  if (string.length < padding) {
    string = string.padStart(padding, "0").toUpperCase();
  } else if (string.length > padding) {
    string = string.substring(string.length - padding);
  }

  return string;
};

const str2hex = function (str, padding = 4) {
  if (str.toUpperCase().includes("0X")) {
    return Number.parseInt(str, 16).toString(16).padStart(padding, "0").toUpperCase();
  } else
    return Number.parseInt(str, 10).toString(16).padStart(padding, "0").toUpperCase();
};

const getGuidsInString = function (str) {
  const regex =
    /(\{){0,1}[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}(\}){0,1}/g;
  return str.match(regex);
};

/**
 *
 * @param {*} date
 * @param {*} utc
 */
const formatDate = (date) => {
  return date.toISOString().split(".")[0];
};

const formatDay = (date) => {
  let d = new Date(date);
  let month = "" + (d.getMonth() + 1);
  let day = "" + d.getDate();
  let year = d.getFullYear();

  if (month.length < 2) month = "0" + month;
  if (day.length < 2) day = "0" + day;

  return [year, month, day].join("-");
};

/**
 *
 * @param {*} date1
 * @param {*} offset
 * @returns
 */
const setOffsetDate = (date1 = new Date(), offset = 60 * 24) => {
  let date2 = new Date(date1);
  return new Date(date2.setMinutes(date1.getMinutes() - offset));
};

const NONE = 0;
const BEFORE = 1;
const AFTER = 2;
const INSIDE = 3;
const APEX = 4;
const NO_ZERO = 5;

const formatTime = (sec, unit = NONE, addZero = true) => {
  let [minutes, seconds] = secondsToTime(sec);

  if (addZero) {
    minutes = minutes < 10 ? "0" + minutes : minutes;
    seconds = seconds < 10 ? "0" + seconds : seconds;
  }

  switch (unit) {
    case BEFORE:
      return "Min: " + minutes + " Sec: " + seconds;
    case AFTER:
      return minutes + ":" + seconds + " (min:sec)";
    case INSIDE:
      return minutes + " min " + seconds + " sec";
    case APEX:
      return minutes + "'" + seconds + "''";
    case NO_ZERO:
      return (
        (minutes > 0 ? minutes + " min" + (seconds > 0 ? ` ${t("generic.and")} ` : "") : "") +
        (seconds > 0 ? seconds + " sec" : "")
      );
    case NONE:
    default:
      return minutes + ":" + seconds;
  }
};

/**
 * Converts a total number of seconds into a time string in the format of "HH:MM:SS".
 *
 * @param {number} totalSeconds - The total number of seconds to be converted.
 * @param {string} [format="hh:mm:ss"] - The desired output format. Defaults to "hh:mm:ss".
 * @return {string} The time string in the format of "HH:MM:SS".
 *
 * @example
 * // returns "01:05:30"
 * secondsToTimeString(3930);
 */
const secondsToTimeString = (totalSeconds, format = "hh:mm:ss") => {
  const hours = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
  const minutes = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
  const seconds = String(totalSeconds % 60).padStart(2, "0");

  return format.toLowerCase()
    .replace("hh", hours)
    .replace("mm", minutes)
    .replace("ss", seconds);
};

/**
 * Return an array with minutes and seconds
 * @param {*} sec
 * @returns
 */
const secondsToTime = (sec) => {
  let minutes = Math.floor(sec / 60);
  let seconds = sec % 60;
  return [minutes, seconds];
};

const isLeap = (year) =>
  year > 1584 &&
  (year % 400 === 0 || (year % 4 === 0 && year % 100 !== 0));

const getAge = (date1, date2, langKey = "license", split = false) => {
  //convert to UTC
  let date1_UTC = new Date(
    Date.UTC(date1.getUTCFullYear(), date1.getUTCMonth(), date1.getUTCDate())
  );

  let date2_UTC = new Date(
    Date.UTC(date2.getUTCFullYear(), date2.getUTCMonth(), date2.getUTCDate())
  );

  if (isLeap(date1_UTC.getFullYear()) || isLeap(date2_UTC.getFullYear())) {
    date2_UTC.setDate(date2_UTC.getDate() + 1);
  }

  let yText, mText, dText;

  //--------------------------------------------------------------
  let days = date2_UTC.getDate() - date1_UTC.getDate();
  if (days < 0) {
    date2_UTC.setMonth(date2_UTC.getMonth() - 1);
    days += daysInMonth(date2_UTC);
  }
  //--------------------------------------------------------------
  let months = date2_UTC.getMonth() - date1_UTC.getMonth();
  if (months < 0) {
    date2_UTC.setFullYear(date2_UTC.getFullYear() - 1);
    months += 12;
  }
  //--------------------------------------------------------------
  let years = date2_UTC.getFullYear() - date1_UTC.getFullYear();

  const getPluralSuffix = (value) => value === 1 ? "" : "s";

  yText =
    years === 0
      ? null
      : t(`${years.toFixed(0)} common:${langKey}.year${getPluralSuffix(years)}`)
  mText =
    months === 0
      ? null
      : t(`${years.toFixed(0)} common:${langKey}.month${getPluralSuffix(months)}`)
  dText =
    days === 0
      ? null
      : t(`${years.toFixed(0)} common:${langKey}.day${getPluralSuffix(days)}`)

  if (split) {
    return [yText, mText, dText].filter((x) => x !== null);
  }

  let duration =
    (yText || "") +
    (mText
      ? yText && dText
        ? ", " + mText
        : yText && !dText
          ? " " + t("common:generic.and") + " " + mText
          : mText
      : "") +
    (dText
      ? yText || mText
        ? " " + t("common:generic.and") + " " + dText
        : dText
      : "");

  return duration;
};

const daysInMonth = (date2_UTC) => {
  let monthStart = new Date(date2_UTC.getFullYear(), date2_UTC.getMonth(), 1);
  let monthEnd = new Date(date2_UTC.getFullYear(), date2_UTC.getMonth() + 1, 1);
  let monthLength = (monthEnd - monthStart) / (1000 * 60 * 60 * 24);
  return monthLength;
};

const convertCamelCase = (str) => {
  return (
    str
      // insert a space before all caps
      .replace(/([A-Z])/g, " $1")
      // uppercase the first character
      .replace(/^./, (firstChar) => {
        return firstChar.toUpperCase();
      })
  );
};

const getLangText = (id, customizationData = null) => {
  if (!id) return "NA";

  if (customizationData?.points?.[id]?.label) {
    return customizationData.points[id].label;
  }

  let pointName = t("common:points." + id);

  if (pointName === "" || pointName.includes("points.")) {
    pointName = convertCamelCase(id);
  }

  return pointName;
};

// Convert value to "test value" by multiplying by 10 if decimal
const convertToTestValue = (value) => {
  return value * (value % 1 === 0 ? 1 : 10);
};

const pointValue = (val, type, vtype, isRelative = false) => {
  if (val === null || val === undefined) return POINT_VALUE.NA;

  let { temp_unit, press_unit } = reduxStore.getState().user;
  const testValue = convertToTestValue(val);

  if (testValue === 32767) return POINT_VALUE.ERROR;
  if (testValue === -32767 || testValue === 32768 || testValue === -32768)
    return POINT_VALUE.NO_PROBE;

  let v2show = val;

  switch (vtype) {
    case "temperature":
      switch (temp_unit) {
        case "Fahrenheit":
          v2show = CToF(v2show, isRelative);
          break;
        default:
          v2show = v2show.toFixed(1);
          break;
      }
      break;
    case "pressure":
      switch (press_unit) {
        case "kPa":
          v2show = barToKpa(v2show);
          break;
        case "MPa":
          v2show = barToMpa(v2show);
          break;
        case "PSI":
          v2show = barToPsi(v2show);
          break;
        default:
          v2show = v2show.toFixed(1);
          break;
      }
      break;
    default:
      break;
  }

  if (type === "bit") {
    if (val === 1) v2show = POINT_VALUE.ON;
    else if (val === 0) v2show = POINT_VALUE.OFF;
    else v2show = POINT_VALUE.ERROR;
  }
  return v2show;
};

const getUnitByType = (val, type = "") => {
  let { temp_unit, press_unit } = reduxStore.getState().user;
  if (
    val === POINT_VALUE.NA ||
    val === POINT_VALUE.ERROR ||
    val === POINT_VALUE.NO_PROBE ||
    val === POINT_VALUE.BAD_LINK
  )
    return "";
  switch (type) {
    case "temperature":
      switch (temp_unit) {
        case "Fahrenheit":
          return " °F";
        case "Celsius":
        default:
          return " °C";
      }
    case "percentage":
      return "%";
    case "pressure":
      switch (press_unit) {
        case "kPa":
          return " kPa";
        case "MPa":
          return " MPa";
        case "PSI":
          return " PSI";
        case "bar":
        default:
          return " bar";
      }
    case "power":
      return " W";
    case "voltage":
      return " V";
    case "current":
      return " A";
    case "apparentPower":
      return " VA";
    case "reactivePower":
      return " VAR";
    case "frequency":
      return " Hz";
    case "energy":
      return " kWh";
    case "reactiveEnergy":
      return " kvarh";
    default:
      return "";
  }
};

const getSelectedUnit = (vtype) => {
  let { temp_unit, press_unit } = reduxStore.getState().user;

  switch (vtype.toLowerCase()) {
    case "temperature":
      return temp_unit || "Celsius";
    case "pressure":
      return press_unit || "bar";
    default:
      return "";
  }
};

const convertValueForThresholds = (val, vtype, isRelative = false) => {
  switch (vtype.toLowerCase()) {
    case "temperature":
      if (getSelectedUnit(vtype.toLowerCase()) === "Celsius") return val
      return FToC(val, isRelative);
    case "pressure": {
      // Thresholds are entered in the selected unit; store them in bar.
      const unit = normalizeUnit("pressure", getSelectedUnit("pressure"));
      if (!unit || unit === "bar") return val;
      return pressureToBar(val, unit);
    }
    default:
      return val;
  }
};

const getModelFromMFVE = (mfve) => {
  return mfve?.split("_")[0];
};

/*
 * A valid MFVE: model_family-version-memory, every segment after the model in
 * hex. The family is captured as the hex group right before version and
 * memory, whatever the model contains. IoT.API validates and reads it with the
 * same pattern (Mfve.IsValid / Mfve.TryGetValidFamily).
 */
const VALID_MFVE_REGEX =
  /^[0-9a-zA-Z_-]+_([0-9a-fA-F]{4})-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}$/;

const checkMfve = (mfve) => VALID_MFVE_REGEX.test(mfve);

const getDeviceInfoFromMFVE = (mfve) => {
  let device = {
    mfve,
    unknown: isModelUnknown(mfve),
    model: null,
    family: null,
    firmwareRelease: null,
    mapLayout: null,
  };

  if (device.unknown) return device;

  const parseIntOrDash = (value, base) => {
    const parsed = Number.parseInt(value, base);
    return Number.isNaN(parsed) ? "-" : parsed;
  };

  const getRel = (rel) => rel % 10 === 0 ? ".0" : "";

  try {
    device.model = getModelFromMFVE(mfve);
    device.family = parseIntOrDash(mfve.split("_")[1].split("-")[0], 16);
    let rel = parseIntOrDash(
      mfve.split("_")[1].split("-")[1].split("-")[0],
      16
    );
    device.firmwareRelease =
      rel === "-" ? rel : rel / 10 + getRel(rel);
    device.mapLayout = parseIntOrDash(mfve.split("_")[1].split("-")[2], 16);
  } catch (e) {
    debug(e);
  }
  return device;
};

const isModelUnknown = (model) => {
  return !!model && model.toLowerCase().startsWith("unknown");
};

const gatewayIsWifiDevice = (gateway) => {
  const devices = Array.isArray(gateway)
    ? gateway
    : gateway?.devices ?? [];

  return devices.some((device) => {
    const mfve = device?.mfve ?? device?.model;
    if (!mfve || !checkMfve(mfve)) return false;

    const { family } = getDeviceInfoFromMFVE(mfve);
    return typeof family === "number" && family === keys.WIFI_DEVICE_FAMILY;
  });
};

/**
 * A single controller that is a WiFi Controller (MFVE family 107): it is its
 * own gateway, so it has no Modbus address worth showing as its Reference.
 * Takes the device as the store holds it (`model`) or as an MFVE (`mfve`).
 */
const isWifiController = (device) =>
  Boolean(device) && gatewayIsWifiDevice([device]);

/**
 * A Dixell device: its MFVE family is below 0x8000. The .bin parameter map is a
 * proprietary Dixell format, so only these devices have one to download or
 * upload. Takes the device as the store holds it (`model`) or as an MFVE (`mfve`).
 */
const isDixellDevice = (device) => {
  const match = VALID_MFVE_REGEX.exec(device?.mfve ?? device?.model ?? "");
  return Boolean(match) && Number.parseInt(match[1], 16) < keys.FIRST_NON_DIXELL_FAMILY;
};

/**
 * Whether a device's Reference — its Modbus address — is worth showing: not
 * for the XIG dongle, nor for a WiFi Controller, which is its own gateway.
 */
const showsModbusReference = (device) =>
  !device?.isDongleController && !isWifiController(device);

const parseReceiverList = (receivers = "") => {
  // Split string after removing all whitespaces and leading/trailing ";" if any
  return receivers
      .replace(/^;+|;+$|\s+/g, "")
      .split(";")
      .filter(Boolean);
};

const getArgonColors = (property) => {
  return getComputedStyle(document.documentElement)
    .getPropertyValue("--" + property)
    .trim();
};

const geocodeReverseAddress = (r) => {
  return {
    address:
      (r.road || "-") +
      ", " +
      (r.city || r.town || r.suburb || r.village || "-") +
      ", " +
      (r.county || "-") +
      ", " +
      (r.state || "-") +
      ", " +
      (r.country || "-"),
    country: r.country,
    city: r.city || r.town || r.suburb || r.village || "-",
  };
};

const capitalize = (s) => {
  if (typeof s !== "string") return "";
  return s.charAt(0).toUpperCase() + s.slice(1);
};

/*******************************
 * addToObject
 * returns the object with a
 * new key and value, placed
 * in a specific position
 *******************************/
const addToObject = (obj, key, value, index) => {
  // Create a new object and index variable
  let newObj = {};
  let i = 0;

  // Loop through the original object
  for (let prop in obj) {
    if (obj.hasOwnProperty(prop)) {
      // If the indexes match, add the new item
      if (i === index && key && value) {
        newObj[key] = value;
      }

      // Add the current item in the loop to newObj
      newObj[prop] = obj[prop];

      // Increase the count
      i++;
    }
  }

  // If no index, add to the end
  if (!index && key && value) {
    newObj[key] = value;
  }

  return newObj;
};

/**
 * getThresholdType: get the Alert type -> 0: Analog - 1: Rising Edge - 2: Falling Edge
 * @param {*} maxValue
 * @param {*} minValue
 * @param {*} minHysteresis
 * @param {*} maxHysteresis
 */
const getThresholdType = (maxValue, minValue, minHysteresis, maxHysteresis) => {
  let threshold = 0; // 0: Analog; 1: Rising Edge; 2: Falling Edge

  if (
    maxValue === 0.5 &&
    maxHysteresis === 0.05 &&
    (minValue < 0 || minValue === null)
  )
    threshold = 1;
  else if (
    minValue === 0.5 &&
    minHysteresis === 0.05 &&
    (maxValue > 1 || maxValue === null)
  )
    threshold = 2;

  return threshold;
};

/**
 * getGatewayRelease
 *
 * @param {*} gatewayGuid
 * @param {boolean} [isAdmin=false]
 * @return {*}
 */
const getGatewayRelease = async (gatewayGuid, isAdmin = false) => {
  const state = reduxStore.getState();

  if (
    isAdmin ||
    !state.gateways.byId[gatewayGuid] ||
    !state.gateways.byId[gatewayGuid].releaseFW
  ) {
    return getGatewayReleaseAsync(gatewayGuid, isAdmin, state);
  } else {
    if (
      state.gateways.byId[gatewayGuid] &&
      state.gateways.byId[gatewayGuid].releaseFW
    ) {
      if (state.user.role === "Admin")
        return state.gateways.byId[gatewayGuid].releaseFW;
      return state.gateways.byId[gatewayGuid].releaseFW.split("-")[0];
    }
  }

  return null;
};

const getGatewayReleaseAsync = async (gatewayGuid, isAdmin, state) => {
  let response = await api.getGatewayTwin(gatewayGuid);

  const device = response?.data?.properties?.reported?.device;

  if (device?.version) {
    if (state.user.role === "Admin" || isAdmin) return device.version[1];
    else return device.version[1].split("-")[0];
  }

  return null;
};

const checkDifferencesOnThreshold = (data, originalData, analog) => {
  let sameReceivers = true;
  let sameAttributes = true;

  const receivers = utils.parseReceiverList(data.receivers);
  const prevReceivers = utils.parseReceiverList(originalData.receivers);

  if (receivers.length === prevReceivers.length) {
    for (let i = 0; i < receivers.length; i++) {
      if (receivers[i] !== prevReceivers[i]) {
        sameReceivers = false;
        break;
      }
    }
  } else {
    sameReceivers = false;
  }

  if (data.alertDelay.upperDelay !== originalData.alertDelay.upperDelay ||
      data.alertDelay.lowerDelay !== originalData.alertDelay.lowerDelay) {
    sameAttributes = false;
  }

  if (analog && sameAttributes) {
    // Check analog
    const parseValue = (value) => {
      const parsed = Number.parseFloat(value);
      return Number.isNaN(parsed) ? null : parsed;
    };
    sameAttributes =
      parseValue(data.maxValue) === parseValue(originalData.maxValue) &&
      parseValue(data.minValue) === parseValue(originalData.minValue) &&
      parseValue(data.minHysteresis) ===
        parseValue(originalData.minHysteresis) &&
      parseValue(data.maxHysteresis) === parseValue(originalData.maxHysteresis);
  } else {
    // Check digital
    Object.keys(data).forEach((item) => {
      if (item !== "receivers") {
        if (data[item] !== originalData[item]) sameAttributes = false;
      }
    });
  }

  return { receivers: sameReceivers, attributes: sameAttributes };
};

// Data conversion
/////////////////////////////////////////////////
const CToF = (value, isRelative = false) => {
  if (isRelative) {
    return ((value * 9) / 5).toFixed(1);
  }
  return ((value * 9) / 5 + 32).toFixed(1);
};

const FToC = (value, isRelative = false) => {
  if (isRelative) {
    return ((value * 5) / 9).toFixed(1);
  }
  return (((value - 32) * 5) / 9).toFixed(2);
};

// The temperature preference is selected/persisted as a numeric index that MUST match
// the TemperatureUnit enum in IoT.DB.Models (Celsius=0, Fahrenheit=1). Shared by the
// user (Preferences) and site (SiteProperties) preference forms so the two cannot drift.
const temperatureUnitToIndex = (unit) =>
  unit?.toLowerCase() === "fahrenheit" ? 1 : 0;

const indexToTemperatureUnit = (num) =>
  Number(num) === 1 ? "Fahrenheit" : "Celsius";

const BAR_PSI_FACTOR = 14.504;

const barToPsi = (value) => {
  return (value * BAR_PSI_FACTOR).toFixed(1);
};

const PsiToBar = (value) => {
  return (value / BAR_PSI_FACTOR).toFixed(2);
};

// kPa / MPa (AB#48219): pure physical factors, 1 bar = 100 kPa = 0.1 MPa.
// Rounding mirrors the bar<->PSI helpers and the IoT.API C# translator so the web
// value and the value written to the controller agree.
const barToKpa = (value) => {
  return (value * 100).toFixed(1);
};

const KpaToBar = (value) => {
  return (value * 0.01).toFixed(2);
};

const barToMpa = (value) => {
  return (value * 0.1).toFixed(1);
};

const MpaToBar = (value) => {
  return (value * 10).toFixed(2);
};

// Convert any supported pressure unit to bar, then from bar to any supported unit.
// Using bar as the pivot keeps every unit pair consistent (and consistent with the
// backend, which stores in bar) without a combinatorial switch.
const pressureToBar = (value, unit) => {
  switch (unit) {
    case "kpa":
      return Number(KpaToBar(value));
    case "mpa":
      return Number(MpaToBar(value));
    case "psi":
      return Number(PsiToBar(value));
    case "bar":
    default:
      return Number(value);
  }
};

const pressureFromBar = (bar, unit) => {
  switch (unit) {
    case "kpa":
      return barToKpa(bar);
    case "mpa":
      return barToMpa(bar);
    case "psi":
      return barToPsi(bar);
    case "bar":
    default:
      return bar;
  }
};

// The pressure preference is selected/persisted as a numeric index that MUST match
// the PressureUnit enum in IoT.DB.Models (Bar=0, PSI=1, KiloPascal=2). Shared by the
// user (Preferences) and site (SiteProperties) preference forms so the two cannot drift.
// Accepts both the "kPa" symbol (core-IAM user string) and the "KiloPascal" enum name
// (GetSite serializes the enum by name via StringEnumConverter). MPa is not a selectable
// preference (device-only unit, handled in the data path).
const pressureUnitToIndex = (unit) => {
  switch (unit?.toLowerCase()) {
    case "kpa":
    case "kilopascal":
      return 2;
    case "psi":
      return 1;
    default:
      return 0;
  }
};

const indexToPressureUnit = (num) => {
  switch (Number(num)) {
    case 2:
      return "kPa";
    case 1:
      return "PSI";
    default:
      return "bar";
  }
};
/////////////////////////////////////////////////

const setLicensesInfo = (licenses = []) => {
  let newLicenses = JSON.parse(JSON.stringify(licenses));

  newLicenses.forEach((license) => {
    const days = license.duration;
    const startDate = parseBackendDate(license.activeFrom);
    const expirationDate = startDate
      ? utils.setOffsetDate(startDate, -(days * 24 * 60))
      : null;

    const timeLeft =
      startDate && expirationDate
        ? utils.getAge(new Date(), expirationDate).toLowerCase()
        : null;
    const timeLeftInt =
      startDate && expirationDate
        ? expirationDate.getTime() - Date.now()
        : null;
    const isExpired =
      license.isActive === 2 ||
      (expirationDate !== null && Date.now() - expirationDate >= 0);
    const difference = Math.ceil(
      (expirationDate - Date.now()) / (1000 * 60 * 60 * 24)
    );
    const isExpiring =
      license.isActive === 1 &&
      !isExpired &&
      difference > 0 &&
      difference <= 28 * 2;
    const isCancelled = license.isActive === 3;

    license.expirationDate = expirationDate;
    license.licenseTime = convertLicenseDaysToHumanReadable(days);
    license.timeLeft = timeLeft;
    license.timeLeftInt = timeLeftInt;
    license.isExpired = isExpired;
    license.isExpiring = isExpiring;
    license.isCancelled = isCancelled;
    license.assigned =
      license.isActive !== 0 ||
      license.activeFrom !== null ||
      license.gateway !== null;
    license.isActive = license.isActive === 1; // pay attention, isActive is a boolean after this statement, not a number anymore
  });

  return newLicenses;
};

const getLicenseIconAndColor = (license) => {
  if (
    !license.isActive &&
    !license.isExpiring &&
    !license.isExpired &&
    !license.isCancelled
  ) {
    return [keys.ICON_OK, "bg-primary"];
  } else if (
    license.isActive &&
    !license.isExpiring &&
    !license.isExpired &&
    !license.isCancelled
  ) {
    return [keys.ICON_OK, "bg-success"];
  } else if (license.isExpired || license.isCancelled) {
    return [keys.ICON_ERROR, "bg-danger"];
  } else if (license.isExpiring) {
    return [keys.ICON_OK, "bg-yellow"];
  } else if (license.activeFrom !== null) {
    return [keys.ICON_LICENSE_DEFERRED, "bg-primary"];
  } else return null;
};

const getSortIcon = (n) => {
  if (n === 0) return keys.ICON_SORT;
  else if (n === 1) return keys.ICON_SORT_ASC;
  else if (n === 2) return keys.ICON_SORT_DESC;
  return null;
};

const addClassToExistingElement = (element, clsname) => {
  return cloneElement(element, {
    className: element.props.className + clsname,
  });
};

// Priority: alarm beats offline beats warn beats ok.
export const getSiteStatus = ({
  alarms = [],
  offlineGateways = [],
  totalGateways = [],
} = {}) => {
  if (totalGateways.length === 0) return "empty";
  if (alarms.length > 0) return "alarm";
  if (offlineGateways.length === 0) return "ok";
  if (offlineGateways.length === totalGateways.length) return "offline";
  return "warn";
};

// Every Site ID starts with it (XpCommon.Audit.SiteUniqueId: ST-{SiteId}-{hex}).
const SITE_ID_PREFIX = "st-";

// Partial match anywhere in the Site ID, like the other site search fields
// (AB#49446), except a piece of the shared prefix alone ("st", "st-", "-"),
// which would list every site. The term must already be trimmed and lowercased.
export const siteUniqueIdIncludes = (site, term) => {
  const id = (site?.uniqueId ?? "").toLowerCase();
  if (!id || !term || SITE_ID_PREFIX.includes(term)) return false;
  return id.includes(term);
};

export const siteUniqueIdMatches = (site, searchTerm) =>
  siteUniqueIdIncludes(site, (searchTerm ?? "").trim().toLowerCase());

const VALID_MAC = 1;
const INVALID_LENGTH = -1;
const INVALID_CHARS = -2;
const INVALID_OUI = -3;

export const checkMac = {
  VALID_MAC,
  INVALID_LENGTH,
  INVALID_CHARS,
  INVALID_OUI,
};

/**
 * Check MAC Address:
 * Verify:
 *  - Length (12)
 *  - Characters
 *  - OUI (00:0A:F6:XX:XX:XX)
 * @param {*} mac
 * @returns {*} int -> 1: OK, -1: Length
 */
const isMACAddressValid = (mac) => {
  // Check MAC Address length
  if (mac.length !== 12) {
    return INVALID_LENGTH;
  }

  let splitMac = mac.match(/.{1,2}/g);

  for (let byte of splitMac) {
    if (Number.isNaN(Number("0x" + byte))) {
      return INVALID_CHARS;
    }
  }

  if (
    splitMac[0].toUpperCase() !== "00" ||
    splitMac[1].toUpperCase() !== "0A" ||
    splitMac[2].toUpperCase() !== "F6"
  ) {
    return INVALID_OUI;
  }

  return VALID_MAC;
};

const getGatewaysForSite = (siteGuid, allGateways) => {
  return Object.keys(allGateways).reduce((acc, key) => {
    const gwSiteGuid = allGateways[key].site?.siteGuid;
    if (gwSiteGuid && gwSiteGuid === siteGuid) {
      acc[key] = allGateways[key];
    }
    return acc;
  }, {});
};

/**
 * Sort array of objects by provided key
 * @param {*} list
 * @param {*} key
 * @returns {array}
 */
const sortArrayOfObjects = (list, key) => {
  return list.sort((a, b) => (a[key] > b[key] ? 1 : a[key] < b[key] ? -1 : 0));
};

const convertLicenseDaysToHumanReadable = (totalDays) => {
  let years = 0,
    months = 0,
    days = 0;

  // 365 days as a year
  if (totalDays >= 365) {
    years = Math.floor(totalDays / 365);
    totalDays %= 365;
  }

  // 30 days as a month
  if (totalDays >= 30) {
    months = Math.floor(totalDays / 30);
    totalDays %= 30;
  }

  days = totalDays;

  let result = "";

  if (years > 0) {
    let yearLabel = t(
      `common:license.year${years > 1 ? "s" : ""}`
    ).toLowerCase();
    result += `${years} ${yearLabel}`;
    if (months > 0 || days > 0) {
      result += ", ";
    }
  }
  if (months > 0) {
    let monthLabel = t(
      `common:license.month${months > 1 ? "s" : ""}`
    ).toLowerCase();
    result += `${months} ${monthLabel}`;
    if (days > 0) {
      result += ` ${t("common:generic.and").toLowerCase()} `;
    }
  }
  if (days > 0) {
    let dayLabel = t(`common:license.day${days > 1 ? "s" : ""}`).toLowerCase();
    result += `${days} ${dayLabel}`;
  }

  return result;
};

const getGatewayLastStatus = (status) => {
  if (!status) return "unknown";
  const { cloud, system, modbus } = status;
  switch (cloud) {
    case 1:
      return "licenseNotValid";
    case 3:
      switch (system) {
        case 0:
          switch (modbus) {
            case 0:
              return "discoveryInProgress";
            case 1:
              return "discoveryCompleted";
            case 2:
              return "loadingConfiguration";
            case 3:
              return "configurationLoaded";
            case 4:
              return "normalRun";
            default:
              return "unknown";
          }
        case 1:
          return "firmwareUpdateInProgress";
        case 2:
        case 3:
        case 4:
          return "rebooting";
        default:
          return "unknown";
      }
    default:
      return "unknown";
  }
};

// Set of gatewayGuids that belong to a hidden site. The active-alarm store
// (alerts.byId) carries no per-alert isHidden flag, so hidden status is resolved
// by joining each alert's gatewayGuid to its owning site — the same join the
// Dashboard map uses — keeping every count consistent with site visibility.
const hiddenGatewayGuids = (sites) => {
  const set = new Set();
  const byId = sites?.byId;
  if (!byId) return set;
  for (const siteGuid in byId) {
    const site = byId[siteGuid];
    if (!site?.isHidden) continue;
    for (const gw of site.gateways || []) {
      if (gw?.gatewayGuid) set.add(gw.gatewayGuid);
    }
  }
  return set;
};

// `onlyGatewayGuids` (optional Set): when given, only alerts on those gateways count,
// e.g. the gateways of the sites currently matching the dashboard filters.
const countAlertsForCustomer = (
  alerts,
  customerGuid,
  includeHiddenSites,
  sites,
  onlyGatewayGuids = null
) => {
  const { byId } = alerts;
  const hiddenGateways = includeHiddenSites ? null : hiddenGatewayGuids(sites);
  let count = 0;

  for (const gatewayGuid in byId) {
    if (onlyGatewayGuids && !onlyGatewayGuids.has(gatewayGuid)) continue;
    // A whole gateway bucket is hidden when its site is hidden.
    if (hiddenGateways?.has(gatewayGuid)) continue;
    const devices = byId[gatewayGuid];

    for (const deviceGuid in devices) {
      const alertList = devices[deviceGuid];

      const filteredAlerts = alertList.filter(
        (alert) =>
          (customerGuid == null || alert.customerGuid === customerGuid) &&
          (includeHiddenSites || !alert.isHidden)
      );

      count += filteredAlerts.length;
    }
  }
  return count;
};

// `date` selects the instant the offset is read at: the same zone can be
// +01:00 or +02:00 depending on DST, so a timestamp must be labelled with the
// offset in force when it happened, not the one in force right now. Defaults to
// "now" for the callers that label live values.
const getTimezone = (localTime = true, date = new Date()) => {
  if (!localTime) return "UTC";

  const reference =
    date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
  const offsetMinutes = -reference.getTimezoneOffset();
  const sign = offsetMinutes >= 0 ? "+" : "-";
  const absMinutes = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absMinutes / 60)).padStart(2, "0");
  const minutes = String(absMinutes % 60).padStart(2, "0");

  return `UTC${sign}${hours}:${minutes}`;
};

const getIanaTimezone = () => {
  try {
    if (typeof Intl !== "undefined" && typeof Intl.DateTimeFormat === "function") {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (timezone) return timezone;
    }
  } catch (error) {
    debug("warn", "Unable to resolve IANA timezone", error);
  }
  return "UTC";
};

export const formatDateTime = (date) => {
  // Ensure date is in RFC2822 or ISO format for moment
  let validDate = date;
  if (typeof date === "string" && !/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(date)) {
    // Try to parse with Date and convert to ISO
    const parsed = new Date(date);
    if (!Number.isNaN(parsed.getTime())) {
      validDate = parsed.toISOString();
    }
  }
  return moment(validDate).format("YYYY-MM-DD HH:mm");
};
// The backend serializes DateTime as ISO 8601 *without* a timezone designator
// (e.g. "2026-07-28T10:28:26") even though the value is UTC, because the DTOs
// use DateTime (Kind=Unspecified) rather than DateTimeOffset. JavaScript reads
// a naive date-time as LOCAL time, so an unconverted value shows the UTC clock
// mislabelled as local.
//
// Add the missing "Z" only when the value carries no timezone information at
// all: a blunt `value + "Z"` would produce "…ZZ" — an Invalid Date — as soon as
// the backend starts emitting the suffix.
export const normalizeIsoUtc = (value) => {
  if (typeof value !== "string") return value;
  const hasTime = value.includes("T");
  const hasTz = /[zZ]$|[+-]\d{2}:?\d{2}$/.test(value);
  // A date-only value is already interpreted as UTC, so leave it alone.
  return hasTime && !hasTz ? `${value}Z` : value;
};

// Parse a backend timestamp into a Date, or null when there is nothing usable
// to show. Returning null rather than an Invalid Date keeps the "no value" and
// "unparseable value" cases in one branch at the call sites.
export const parseBackendDate = (value) => {
  if (value === null || value === undefined || value === "") return null;
  let d;
  if (value instanceof Date) {
    d = value;
  } else if (typeof value === "string") {
    d = new Date(normalizeIsoUtc(value));
  } else {
    return null;
  }
  return Number.isNaN(d.getTime()) ? null : d;
};

// Earliest year we consider a real, meaningful timestamp. The backend sends the
// .NET default DateTime ("0001-01-01T00:00:00") — and, occasionally, other
// nonsensical low dates — when a value was never actually set (e.g. a gateway
// that provisioned a device but never received data). Anything before this year
// is treated as "no valid information".
const PLAUSIBLE_DATE_MIN_YEAR = 2000;

export const isPlausibleDate = (value) => {
  // parseBackendDate treats a naive date-time as UTC, so the plausibility
  // boundary stays timezone-independent.
  const d = parseBackendDate(value);
  if (d === null) return false;
  return d.getUTCFullYear() >= PLAUSIBLE_DATE_MIN_YEAR;
};

export const formatValueWithUnit = (value, unit) => {
  if (value === undefined || value === null || value === "") return "";

  const normalizedUnit = typeof unit === "string" ? unit.trim() : "";
  const valueStr = typeof value === "string" ? value : String(value);

  return normalizedUnit ? `${valueStr} ${normalizedUnit}` : valueStr;
};

const JUST_NOW_THRESHOLD_SECS = 60;

export const formatRelativeTime = (date, now = Date.now()) => {
  if (date === null || date === undefined || date === "") return null;

  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return null;

  const diffSecs = Math.floor((now - d.getTime()) / 1000);
  if (diffSecs < JUST_NOW_THRESHOLD_SECS) return "justNow";

  const rtf = new Intl.RelativeTimeFormat(i18next.language ?? undefined, { numeric: "auto" });

  const diffMin = Math.round(diffSecs / 60);
  if (diffMin < 60) {
    return rtf.format(-diffMin, "minute");
  }

  // Up to 47 hours we keep counting in hours; from 48 hours on we switch to days.
  const diffHours = Math.round(diffSecs / 3600);
  if (diffHours < 48) {
    return rtf.format(-diffHours, "hour");
  }

  const diffDays = Math.round(diffSecs / 86400);
  return rtf.format(-diffDays, "day");
};

export const truncateFloat = (num, decimals = 1) => {
  const factor = Math.pow(10, decimals);
  return Math.trunc(num * factor) / factor;
}

export const normalizeUnit = (vtype, unit) => {
  if (!unit) return "";
  const u = unit.toString().trim().toLowerCase();

  if (vtype === "temperature") {
    if (u === "°c" || u === "c" || u === "celsius") return "c";
    if (u === "°f" || u === "f" || u === "fahrenheit") return "f";
    return u;
  }

  if (vtype === "pressure") {
    if (u === "bar") return "bar";
    if (u === "psi") return "psi";
    return u;
  }

  return u;
};

const getUserUnitForVtype = (vtype) => {
  if (vtype === "temperature") {
    return normalizeUnit(vtype, getSelectedUnit("temperature"));
  }
  if (vtype === "pressure") {
    return normalizeUnit(vtype, getSelectedUnit("pressure"));
  }
  return "";
};

const getDeviceUnitForVtype = (vtype, deviceUnits) => {
  if (vtype === "temperature") {
    return normalizeUnit(vtype, deviceUnits?.temperature);
  }
  if (vtype === "pressure") {
    return normalizeUnit(vtype, deviceUnits?.pressure);
  }
  return "";
};

const needsUnitConversion = (vtype, userUnit, deviceUnit) => {
  if (vtype !== "temperature" && vtype !== "pressure") return false;
  if (!userUnit || !deviceUnit) return false;
  return userUnit !== deviceUnit;
};

const convertValueToDeviceUnit = (value, vtype, userUnit, deviceUnit, isRelative) => {
  if (!userUnit || !deviceUnit || userUnit === deviceUnit) return value;

  if (vtype === "temperature") {
    if (userUnit === "c" && deviceUnit === "f")
      return CToF(value, isRelative);
    if (userUnit === "f" && deviceUnit === "c")
      return FToC(value, isRelative);
    return value;
  }

  if (vtype === "pressure") {
    // bar, psi, kpa, mpa — pivot through bar so every pair is handled.
    return pressureFromBar(pressureToBar(value, userUnit), deviceUnit);
  }

  return value;
};

const getConvertedValueIfNeeded = (value, vtype, deviceUnits, isRelative) => {
  const userUnit = getUserUnitForVtype(vtype);
  const deviceUnit = getDeviceUnitForVtype(vtype, deviceUnits);

  if (!needsUnitConversion(vtype, userUnit, deviceUnit)) return value;

  return convertValueToDeviceUnit(value, vtype, userUnit, deviceUnit, isRelative);
};

const getDeviceUnitScale = (vtype, deviceUnit, fahrenheitAsDecimal) => {
  if (vtype === "temperature") {
    if (deviceUnit === "c") return 10;
    if (deviceUnit === "f") return fahrenheitAsDecimal ? 10 : 1;
  }
  if (vtype === "pressure") {
    // Metric pressure (bar, kPa, MPa) stores one decimal on the register (ratio 0.1
    // -> scale 10). PSI is the exception: its bar<->PSI factor already folds in the
    // 0.1 ratio, so it keeps integer register arithmetic (scale 1). Mirrors the
    // IoT.API GetDeviceUnitScale default.
    if (deviceUnit === "psi") return 1;
    return 10;
  }
  return 1;
};

const getValueInDeviceUnit = (rawValue, vtype, deviceUnits, isRelative) => {
  if (vtype !== "temperature" && vtype !== "pressure") return rawValue;

  if (rawValue === "" || rawValue === null || rawValue === undefined)
    return rawValue;

  const numericValue = Number(rawValue);

  if (Number.isNaN(numericValue)) return rawValue;

  return getConvertedValueIfNeeded(numericValue, vtype, deviceUnits, isRelative);
};

const normalizeMqttApiFleets = (fleets) => {
  const list = Array.isArray(fleets) ? fleets : [];
  return list
    .map((fleet) => ({
      fleetGuid: fleet?.fleetGuid ?? null,
      fleetName: String(fleet?.fleetName ?? "").trim(),
    }))
    .filter((fleet) => fleet.fleetGuid && fleet.fleetName);
};

const EMAIL_REGEX = /[\w.+-]+@[\w-]+(?:\.[\w-]+)+/g;

const extractEmailsFromMessage = (message) => message?.match(EMAIL_REGEX) || [];

const getUnregisteredEmails = (err) => {
  if (err?.data?.title !== "Groups.UserNotFound") return [];
  return extractEmailsFromMessage(err.data.detail);
};

const resolveUnknownEmailsMessage = (err, t) => {
  const emails = getUnregisteredEmails(err);
  if (emails.length === 0) return null;

  return t("accessRights.notRegisteredUsersError", { emails: emails.join(", ") });
};

/**
 * Turns a failed membership call — assigning a role, adding users — into something to show.
 * Kept out of the component so the mapping can be exercised on its own: these branches are the
 * difference between a useful sentence and a bare "Forbidden".
 */
const resolveMembershipFailure = (err, t) => {
  const code = err?.data?.title;

  if (code === "Subscriptions.UserLimitExceeded") {
    return {
      message: t("accessRights.subscriptionUserLimitExceededError"),
      type: "warning",
    };
  }

  if (code === "Groups.UserNotFound") {
    return {
      message:
        resolveUnknownEmailsMessage(err, t) ||
        t("accessRights.notRegisteredUsersErrorGeneric"),
      type: "warning",
    };
  }

  return {
    message: err?.responseString || "Unknown error",
    type: "danger",
  };
};

/**
 * Roles in the order a person expects to read them. Copies rather than sorts in place: these
 * arrive as a prop, and reordering the caller's array would change what the parent holds.
 */
const sortRolesByName = (roles) =>
  [...(roles ?? [])].sort((a, b) =>
    (a?.name ?? "").localeCompare(b?.name ?? "", undefined, { sensitivity: "base" }),
  );

const utils = {
  debug,
  sortRolesByName,
  extractEmailsFromMessage,
  getUnregisteredEmails,
  resolveUnknownEmailsMessage,
  resolveMembershipFailure,
  isChrome,
  isUnauthPath,
  isDevEnvironment,
  isUserAdmin,
  int2hex,
  str2hex,
  getGuidsInString,
  MSG_TYPE_LOG,
  MSG_TYPE_WARN,
  MSG_TYPE_ERR,
  formatDate,
  formatDay,
  setOffsetDate,
  formatTime,
  secondsToTime,
  secondsToTimeString,
  getAge,
  NONE,
  BEFORE,
  AFTER,
  INSIDE,
  APEX,
  NO_ZERO,
  getLangText,
  pointValue,
  getUnitByType,
  getSelectedUnit,
  convertValueForThresholds,
  getModelFromMFVE,
  isModelUnknown,
  checkMfve,
  getDeviceInfoFromMFVE,
  gatewayIsWifiDevice,
  isWifiController,
  isDixellDevice,
  showsModbusReference,
  parseReceiverList,
  getArgonColors,
  geocodeReverseAddress,
  capitalize,
  addToObject,
  getThresholdType,
  getGatewayRelease,
  getGatewayReleaseAsync,
  checkDifferencesOnThreshold,
  CToF,
  FToC,
  temperatureUnitToIndex,
  indexToTemperatureUnit,
  barToPsi,
  PsiToBar,
  barToKpa,
  KpaToBar,
  barToMpa,
  MpaToBar,
  pressureUnitToIndex,
  indexToPressureUnit,
  setLicensesInfo,
  getLicenseIconAndColor,
  getSortIcon,
  addClassToExistingElement,
  getSiteStatus,
  isMACAddressValid,
  sortArrayOfObjects,
  getGatewaysForSite,
  convertLicenseDaysToHumanReadable,
  getGatewayLastStatus,
  countAlertsForCustomer,
  getTimezone,
  getIanaTimezone,
  formatDateTime,
  formatRelativeTime,
  normalizeIsoUtc,
  parseBackendDate,
  isPlausibleDate,
  formatValueWithUnit,
  truncateFloat,
  normalizeUnit,
  getUserUnitForVtype,
  getDeviceUnitForVtype,
  needsUnitConversion,
  convertValueToDeviceUnit,
  getConvertedValueIfNeeded,
  getDeviceUnitScale,
  getValueInDeviceUnit,
  normalizeMqttApiFleets,
};

export default utils;
